import { Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { usePostHog } from "@posthog/react";
import { useMutation } from "@tanstack/react-query";
import { Auth } from "aws-amplify";
import { Form, Formik } from "formik";
import { replace } from "lodash/fp";
import React, { createRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import * as Routes from "routes";
import { buildPardotIframeUrl } from "utils/pardot";
import { boolean as bool, object, ref, string } from "yup";
import useDarkMode from "use-dark-mode";
import "./style.css";

export const signUpValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
  firstName: string().required("First Name is required."),
  lastName: string().required("Last Name is required."),
  password: string()
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required."),
  confirmPassword: string()
    .oneOf([ref("password"), null], "Passwords must match")
    .required("Confirm Password is required."),
  termsOfUse: bool().oneOf(
    [true],
    "In order to continue, you must accept Geoscape Hub's Terms of Use.",
  ),
});

type SignUpValues = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  termsOfUse: boolean;
};

function trimmessage(message: string): string {
  return replace(
    "..",
    ".",
    replace("PreSignUp failed with error ", "", message),
  );
}

export const SignUp = () => {
  const [recaptchaToken, setRecaptchaToken] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [pardotIframeUrl, setPardotIframeUrl] = useState<string | null>(null);
  const posthog = usePostHog();
  const { value: isDark } = useDarkMode();

  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey: string = import.meta.env.VITE_RECAPTCHA_KEY || "EMPTY";

  const signUpMutation = useMutation({
    mutationFn: async (values: SignUpValues & { recaptchaToken: string }) => {
      if (!values.recaptchaToken) {
        throw new Error("Please complete the reCaptcha challenge.");
      }

      const data = await Auth.signUp({
        username: values.email.toLowerCase(),
        password: values.password,
        attributes: {
          given_name: values.firstName,
          family_name: values.lastName,
          "custom:dev_t_and_c": String(true),
        },
        validationData: {
          recaptcha_token: values.recaptchaToken,
        },
      });

      return {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        userId: data.userSub,
      };
    },
    onSuccess: (data) => {
      posthog.capture("user_signed_up");
      posthog.identify(undefined, {
        email: data.email,
      });

      const iframeUrl = buildPardotIframeUrl(
        import.meta.env.VITE_PARDOT_SIGNUP_FORM_HANDLER_URL,
        {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          userId: data.userId,
        },
      );

      if (iframeUrl) {
        setPardotIframeUrl(iframeUrl);
      }
    },
  });

  const handleVerify = () => {
    const token = recaptchaRef?.current?.getValue();
    setRecaptchaToken(token || "");
    recaptchaRef?.current?.reset();
  };

  const handleSubmit = (values: SignUpValues) => {
    signUpMutation.mutate({ ...values, recaptchaToken });
  };

  const getErrorMessage = (error: any): string => {
    if (!recaptchaToken) {
      return "Please complete the reCaptcha challenge.";
    }
    return trimmessage(error?.message || "An error occurred during sign up");
  };

  const renderPasswordToggle = () => (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="absolute right-1 top-1/2 -translate-y-1/2"
      onClick={() => setShowPassword(!showPassword)}
    >
      {showPassword ? (
        <EyeOff className="size-4" />
      ) : (
        <Eye className="size-4" />
      )}
    </Button>
  );

  return (
    <>
      <Formik<SignUpValues>
        initialValues={{
          email: "",
          firstName: "",
          lastName: "",
          password: "",
          confirmPassword: "",
          termsOfUse: false,
        }}
        onSubmit={handleSubmit}
        validationSchema={signUpValidationSchema}
      >
        {({
          values: {
            email,
            firstName,
            lastName,
            password,
            confirmPassword,
            termsOfUse,
          },
          handleChange,
          handleBlur,
          errors,
          touched,
          setFieldValue,
        }) => (
          <Form>
            <div className="flex flex-col">
              <Card className="min-w-85 max-w-125 m-2">
                <CardHeader>
                  <CardTitle className="text-xl">
                    Create your Geoscape Hub account
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="text"
                      value={email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={signUpMutation.isPending}
                      className={
                        touched.email && errors.email
                          ? "border-destructive"
                          : ""
                      }
                    />
                    {touched.email && errors.email && (
                      <p className="text-xs text-destructive">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={signUpMutation.isPending}
                      className={
                        touched.firstName && errors.firstName
                          ? "border-destructive"
                          : ""
                      }
                    />
                    {touched.firstName && errors.firstName && (
                      <p className="text-xs text-destructive">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={signUpMutation.isPending}
                      className={
                        touched.lastName && errors.lastName
                          ? "border-destructive"
                          : ""
                      }
                    />
                    {touched.lastName && errors.lastName && (
                      <p className="text-xs text-destructive">
                        {errors.lastName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={signUpMutation.isPending}
                        className={`pr-10 ${touched.password && errors.password ? "border-destructive" : ""}`}
                      />
                      {renderPasswordToggle()}
                    </div>
                    {touched.password && errors.password && (
                      <p className="text-xs text-destructive">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={signUpMutation.isPending}
                        className={`pr-10 ${touched.confirmPassword && errors.confirmPassword ? "border-destructive" : ""}`}
                      />
                      {renderPasswordToggle()}
                    </div>
                    {touched.confirmPassword && errors.confirmPassword && (
                      <p className="text-xs text-destructive">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="termsOfUse"
                      checked={termsOfUse}
                      onCheckedChange={(checked) =>
                        setFieldValue("termsOfUse", checked)
                      }
                    />
                    <Label htmlFor="termsOfUse" className="text-sm">
                      {"I agree to Geoscape Hub's "}
                      <a
                        href="https://geoscape.com.au/wp-content/uploads/2025/05/Geoscape-Australia-Geoscape-General-Terms-of-Use-v2.0-July-2025.pdf"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline underline-offset-4"
                      >
                        Terms of Use
                      </a>
                    </Label>
                  </div>
                  {errors.termsOfUse && (
                    <p className="text-xs text-destructive">
                      {errors.termsOfUse}
                    </p>
                  )}

                  <div className="flex justify-center w-full">
                    <ReCAPTCHA
                      id="recaptcha"
                      ref={recaptchaRef}
                      sitekey={recaptchaKey}
                      onChange={handleVerify}
                      theme={isDark ? "dark" : "light"}
                    />
                  </div>

                  {signUpMutation.isPending ? (
                    <div className="flex justify-center w-full py-2">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {signUpMutation.isSuccess && (
                        <Alert variant="success">
                          <AlertDescription>
                            A verification email has been sent to:{" "}
                            {signUpMutation.data?.email}
                          </AlertDescription>
                        </Alert>
                      )}

                      {signUpMutation.isError && (
                        <Alert variant="destructive">
                          <AlertDescription>
                            {getErrorMessage(signUpMutation.error)}
                          </AlertDescription>
                        </Alert>
                      )}

                      <Button
                        type="submit"
                        disabled={signUpMutation.isPending}
                        className="w-full"
                      >
                        Sign Up
                      </Button>

                      <Button
                        variant="link"
                        size="sm"
                        className="w-full"
                        asChild
                      >
                        <a href={Routes.signIn}>Sign In</a>
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </Form>
        )}
      </Formik>

      {pardotIframeUrl && (
        <iframe
          src={pardotIframeUrl}
          width="1"
          height="1"
          style={{ display: "none" }}
          title="Pardot tracking"
        />
      )}
    </>
  );
};
